import React from 'react'
import { Box, TextField, IconButton, Button, Stack, useTheme as useMuiTheme, useMediaQuery, Typography } from '@mui/material'
import { Delete, Add } from '@mui/icons-material'
import { useTheme } from '../../context/ThemeContext'

const TextEditorSkills = ({ entries, onEntriesChange, sectionType }) => {
  const { isDarkMode } = useTheme()
  const theme = useMuiTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleContentChange = (index, value) => {
    const newEntries = [...entries]
    newEntries[index] = {
      ...newEntries[index],
      content: value,
    }
    onEntriesChange(newEntries)
  }

  const handleAddEntry = () => {
    onEntriesChange([
      ...entries,
      {
        id: Date.now(),
        content: '',
      },
    ])
  }

  const handleDeleteEntry = (index) => {
    const newEntries = entries.filter((_, i) => i !== index)
    onEntriesChange(newEntries)
  }

  // Common textfield styling to maintain consistency
  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
    },
    '& .MuiInputBase-root': {
      fontSize: { xs: '0.9rem', sm: '1rem' },
    },
  }

  return (
    <Stack spacing={2}>
      <Typography
        variant='h6'
        sx={{
          color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
          mb: 1,
          fontWeight: 500,
        }}
      >
        {sectionType} Information
      </Typography>

      {entries.map((entry, index) => (
        <Box
          key={entry.id}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            position: 'relative',
            width: '100%',
            p: 2,
            borderRadius: 1,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          {/* Main Text Field */}
          <TextField
            multiline={true}
            rows={5}
            fullWidth
            value={entry.content}
            onChange={(e) => handleContentChange(index, e.target.value)}
            label={`${sectionType} Entry ${index + 1}`}
            sx={textFieldStyle}
          />

          {/* Delete Button */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 2,
              width: '100%',
            }}
          >
            <IconButton
              onClick={() => handleDeleteEntry(index)}
              sx={{
                color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                },
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
      ))}

      {/* Add Button */}
      <Button
        variant='outlined'
        onClick={handleAddEntry}
        startIcon={<Add />}
        sx={{
          alignSelf: 'stretch',
          py: { xs: 1.5, sm: 1 },
          mt: { xs: 1, sm: 2 },
          borderColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
          color: theme.palette.mode === 'dark' ? 'white' : 'secondary.main',
          '&:hover': {
            borderColor: 'secondary.dark',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(65, 226, 186, 0.1)' : 'rgba(111, 0, 255, 0.04)',
            color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
          },
        }}
      >
        Add Skill
      </Button>
    </Stack>
  )
}

export default TextEditorSkills
